import { AccountFeatures } from '@shared/api/types';

/**
 * Sections that can be locked at the group level when the parent org's plan
 * doesn't include the corresponding feature.
 */
export type GroupSectionKey = 'company' | 'purchasing' | 'products';

/**
 * Maps each gateable section to the AccountFeatures key that unlocks it. The
 * sidebar reads these to decide whether to render a section as locked.
 */
export const SECTION_FEATURE_KEYS: Record<
  GroupSectionKey,
  keyof AccountFeatures
> = {
  company: 'ccf',
  purchasing: 'scope3',
  products: 'pcf',
};

/**
 * Human-readable label for each section, shown above the page title on the
 * group dashboard pages (acts as a one-level static "breadcrumb").
 */
export const SECTION_LABELS: Record<GroupSectionKey, string> = {
  company: 'Company',
  purchasing: 'Purchased goods',
  products: 'Products',
};

/**
 * Centralised route table for the v2 group dashboard. The sidebar and the
 * global filter wrapper both read from here so per-page rules (title, which
 * filters apply, section gating) live in one place.
 */
export const GROUP_ROUTES = {
  DASHBOARD: '/group',
  ORGANIZATIONS: '/group/organisations',
  /** Nested users list for a single org; reached only via the org table. */
  ORGANIZATION_USERS: '/group/organisations/:org_uuid/users',
  COMPANY_EMISSIONS: '/group/company/emissions',
  COMPANY_REPORTS: '/group/company/reports',
  PURCHASING_EMISSIONS: '/group/purchasing/emissions',
  PURCHASING_FOOD_AND_DRINK: '/group/purchasing/food-and-drink',
  PURCHASING_PACKAGING: '/group/purchasing/packaging',
  PRODUCTS: '/group/products',
  PRODUCTS_COLLECTIONS: '/group/products/collections',
  SETTINGS: '/group/settings',
} as const;

export type GroupRoutePath = (typeof GROUP_ROUTES)[keyof typeof GROUP_ROUTES];

/**
 * Concrete URL for an organisation's users page. The org name is carried as a
 * query param so the page can render its title/breadcrumb without an extra
 * fetch and still survive a refresh / shared link.
 */
export function buildOrganizationUsersUrl(
  orgUuid: string,
  orgName: string
): string {
  const path = GROUP_ROUTES.ORGANIZATION_USERS.replace(':org_uuid', orgUuid);
  return `${path}?org=${encodeURIComponent(orgName)}`;
}

type PageConfig = {
  path: GroupRoutePath;
  title: string;
  /** Filters rendered on this page. */
  filters: { orgs: boolean; periods: boolean };
  /** If set, page is hidden / locked unless that section is unlocked. */
  section?: GroupSectionKey;
};

export const GROUP_PAGES: Record<string, PageConfig> = {
  dashboard: {
    path: GROUP_ROUTES.DASHBOARD,
    title: 'Group dashboard',
    filters: { orgs: false, periods: false },
  },
  organizations: {
    path: GROUP_ROUTES.ORGANIZATIONS,
    title: 'Organisations',
    filters: { orgs: false, periods: false },
  },
  companyEmissions: {
    path: GROUP_ROUTES.COMPANY_EMISSIONS,
    title: 'Company emissions',
    filters: { orgs: true, periods: true },
    section: 'company',
  },
  companyReports: {
    path: GROUP_ROUTES.COMPANY_REPORTS,
    title: 'Company reports',
    filters: { orgs: true, periods: true },
    section: 'company',
  },
  purchasingEmissions: {
    path: GROUP_ROUTES.PURCHASING_EMISSIONS,
    title: 'Purchasing emissions',
    filters: { orgs: true, periods: true },
    section: 'purchasing',
  },
  purchasingFoodAndDrink: {
    path: GROUP_ROUTES.PURCHASING_FOOD_AND_DRINK,
    title: 'Food and drink',
    filters: { orgs: true, periods: true },
    section: 'purchasing',
  },
  purchasingPackaging: {
    path: GROUP_ROUTES.PURCHASING_PACKAGING,
    title: 'Packaging',
    filters: { orgs: true, periods: true },
    section: 'purchasing',
  },
  products: {
    path: GROUP_ROUTES.PRODUCTS,
    title: 'All products',
    filters: { orgs: true, periods: false },
    section: 'products',
  },
  productsCollections: {
    path: GROUP_ROUTES.PRODUCTS_COLLECTIONS,
    title: 'Collections',
    filters: { orgs: true, periods: false },
    section: 'products',
  },
} as const;

export function isSectionUnlocked(
  section: GroupSectionKey,
  features: AccountFeatures | null
): boolean {
  if (!features) return false;
  return features[SECTION_FEATURE_KEYS[section]];
}
